import React, { useMemo } from 'react';
import { SearchIcon } from '@chakra-ui/icons';
import { ActionMeta, chakraComponents, GroupBase, Select, SelectInstance, StylesConfig } from 'chakra-react-select';

import { useRetailers } from '../../features/bills/tariffs/tariffApi';
import { EMPTY_RETAILER_OPTION } from '../constants';

type RetailerOption = {
  label: string;
  value: number;
};

type SelectRetailerProps = {
  value: number | null;
  onChange: (value: RetailerOption | null) => void;
  onBlur?: () => void;
  ref?: React.Ref<SelectInstance<RetailerOption, false, GroupBase<RetailerOption>>>;
};

export const SelectRetailer = ({ ref, value, onChange, onBlur }: SelectRetailerProps) => {
  const { retailers, isLoading } = useRetailers();

  const selectOptions = useMemo(() => {
    const retailerList = retailers.map((retailer) => ({ label: retailer.name, value: retailer.id }));
    return [EMPTY_RETAILER_OPTION, ...retailerList];
  }, [retailers]);

  const selectedValue = useMemo(() => {
    if (value === null) return null;
    const selectedRetailer = retailers.find((retailer) => retailer.id === value);
    return selectedRetailer ? { label: selectedRetailer.name, value: selectedRetailer.id } : null;
  }, [value, retailers]);

  return (
    <Select
      ref={ref as React.RefCallback<unknown>}
      onChange={onChange as (newValue: unknown, actionMeta: ActionMeta<unknown>) => void}
      isLoading={isLoading}
      placeholder={'Select your retailer..'}
      onBlur={onBlur}
      // Not specifying portal target can cause the menu to become hidden.
      menuPortalTarget={document.body}
      options={selectOptions}
      value={selectedValue}
      styles={
        {
          menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999999,
          }),
        } as StylesConfig
      }
      useBasicStyles
      components={{
        DropdownIndicator: (props) => (
          <chakraComponents.DropdownIndicator {...props}>
            <SearchIcon />
          </chakraComponents.DropdownIndicator>
        ),
      }}
    />
  );
};
